/**
 * Variables de texto del People Landing (Fase 2.5) — función PURA, sin React,
 * para poder testearla desde el backend y usarla en el front.
 *
 * People escribe "Hola, {{primer_nombre}}" y cada persona ve su nombre. Si un
 * dato no existe la variable se reemplaza por vacío y se limpia la puntuación
 * sobrante ("Hola, ." → "Hola.").
 */
export interface LandingVars {
  nombre?: string;
  primer_nombre?: string;
  team?: string;
  clapbase?: string;
  dia?: string | number;
}

export const LANDING_VAR_NAMES = ['nombre', 'primer_nombre', 'team', 'clapbase', 'dia'] as const;

/**
 * Nombres que la traducción automática (o una persona escribiendo en inglés/portugués)
 * le pone a las variables. Incidente 08/10/2026: el traductor convirtió {{primer_nombre}}
 * en {{first_name}} / {{primeiro_nome}}, la variable dejó de existir y se veía cruda; en
 * portugués la "arreglaron" escribiendo un nombre fijo y todos pasaron a ver "Mariana".
 * Se aceptan para siempre (contenido viejo) y el traductor ya no las toca.
 */
const VAR_ALIASES: Record<string, (typeof LANDING_VAR_NAMES)[number]> = {
  nombre: 'nombre', name: 'nombre', nome: 'nombre', full_name: 'nombre', fullname: 'nombre', nombre_completo: 'nombre', nome_completo: 'nombre',
  primer_nombre: 'primer_nombre', first_name: 'primer_nombre', firstname: 'primer_nombre', primeiro_nome: 'primer_nombre', primer_name: 'primer_nombre',
  team: 'team', equipo: 'team', equipe: 'team',
  clapbase: 'clapbase',
  dia: 'dia', day: 'dia', 'día': 'dia',
};

const VAR_RE = /\{\{\s*([A-Za-zÀ-ÿ_]+)\s*\}\}/g;
const canonicalVar = (raw: string) => VAR_ALIASES[raw.trim().toLowerCase()] ?? null;

/** Reescribe las variables traducidas a su nombre canónico ({{first_name}} → {{primer_nombre}}). */
export function canonicalizeLandingVars(text: string): string {
  if (!text || !text.includes('{{')) return text;
  return text.replace(VAR_RE, (all, name: string) => {
    const c = canonicalVar(name);
    return c ? `{{${c}}}` : all;
  });
}

/**
 * Antes de mandar un texto a traducir: cada variable pasa a un marcador opaco (⟦0⟧, ⟦1⟧…)
 * que el modelo no tiene cómo traducir, y `restore` la devuelve con su nombre canónico.
 * Si el modelo se comió un marcador, la variable se agrega al final antes que perderse.
 */
export function protectLandingVars(text: string): { text: string; restore: (translated: string) => string } {
  const vars: string[] = [];
  const masked = (text ?? '').replace(VAR_RE, (all, name: string) => {
    const c = canonicalVar(name);
    if (!c) return all;
    vars.push(`{{${c}}}`);
    return `⟦${vars.length - 1}⟧`;
  });
  return {
    text: masked,
    restore: (translated: string) => {
      let out = canonicalizeLandingVars(translated ?? '');
      vars.forEach((v, i) => {
        const tok = `⟦${i}⟧`;
        if (out.includes(tok)) out = out.split(tok).join(v);
        else if (!out.includes(v)) out = `${out} ${v}`.trim();
      });
      return out;
    },
  };
}

export function applyLandingVars(text: string, vars: LandingVars): string;
export function applyLandingVars(text: string | undefined, vars: LandingVars): string | undefined;
export function applyLandingVars(text: string | undefined, vars: LandingVars): string | undefined {
  if (!text || !text.includes('{{')) return text;
  let missing = false;
  const out = text.replace(VAR_RE, (all, raw: string) => {
    const name = canonicalVar(raw);
    if (!name) return all; // {{algo}} que no es variable: se deja como está
    const v = vars[name];
    if (v === undefined || v === null || String(v).trim() === '') {
      missing = true;
      return '';
    }
    return String(v);
  });
  return missing ? tidy(out) : out;
}

/** Limpia lo que deja una variable vacía: espacios dobles, ", ." y paréntesis vacíos. */
function tidy(s: string): string {
  return s
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/([,;:])\s*([.!?])/g, '$2')
    .replace(/([,;:])(\s*[,;:])+/g, '$1')
    .replace(/(^|\n)[ \t]*[,;:]\s*/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+(\n|$)/g, '$1')
    .trim();
}
